import React, { useEffect, useState } from "react";
import { usePolly } from "@/hooks/usePolly";
import { useAuthContext } from "@/providers/AuthProvider";

interface AudioIconPlayerProps {
  text: string;
  voiceId?: string;
  languageCode?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const AudioIconPlayer: React.FC<AudioIconPlayerProps> = ({
  text,
  voiceId,
  size = "md",
  className = "",
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioController, setAudioController] = useState<any>(null);
  const { user } = useAuthContext();
  const { speakText, isLoading } = usePolly();

  // Utiliser la voix préférée de l'utilisateur si aucune n'est spécifiée
  const effectiveVoiceId = voiceId || (user && user.preferredVoiceId) || "Lea";

  // Nettoyer l'audio controller lors du démontage du composant
  useEffect(() => {
    return () => {
      if (audioController && audioController.stop) {
        audioController.stop();
      }
    };
  }, [audioController]);

  // Taille de l'icône en fonction de la prop size
  const iconSizeClass = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  }[size];

  // Taille du bouton en fonction de la prop size
  const buttonSizeClass = {
    sm: "btn-sm",
    md: "",
    lg: "btn-lg",
  }[size];

  const handlePlayEnded = () => {
    setIsPlaying(false);
    setAudioController(null);
  };

  const handleTogglePlay = async () => {
    if (isPlaying && audioController) {
      // Si une lecture est en cours, on l'arrête
      if (audioController.stop) {
        audioController.stop();
      } else if (audioController.audio) {
        audioController.audio.pause();
        audioController.audio.currentTime = 0;
      }
      setAudioController(null);
      setIsPlaying(false);
    } else {
      // Sinon, on lance une nouvelle lecture
      setIsPlaying(true);

      try {
        const controller = await speakText({
          text,
          voiceId: effectiveVoiceId,
          outputFormat: "mp3",
        });

        setAudioController(controller);

        // Configurer la fin de lecture
        if (controller && controller.audio) {
          controller.audio.onended = () => {
            handlePlayEnded();
          };
        }
      } catch (error) {
        console.error("Erreur lors de la lecture audio:", error);
        setIsPlaying(false);
      }
    }
  };

  return (
    <div className={`audio-icon-player ${className}`}>
      <button
        className={`btn btn-circle ${buttonSizeClass} ${
          isPlaying ? "btn-primary" : "btn-ghost"
        }`}
        onClick={handleTogglePlay}
        disabled={isLoading}
        title={isPlaying ? "Arrêter la lecture" : "Lire à haute voix"}
      >
        {isLoading ? (
          <svg
            className={`animate-spin ${iconSizeClass}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : isPlaying ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconSizeClass}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18V6h4v12H6zm8-12h4v12h-4V6z"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={iconSizeClass}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5 10v4a1 1 0 001 1h3l3.5 3.5a1 1 0 001.5-.79V6.29a1 1 0 00-1.5-.79L9 9H6a1 1 0 00-1 1z"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export default AudioIconPlayer;
